import { useContext, createContext, useMemo, useCallback } from "react"
import { useDispatch } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { Creators as ApiWrapperCreators } from "model/apiWrapper"
import { Creators as PreferencesCreators } from "model/preferences"
import { useAccountId } from "resources/account"
import { useLastPreferenceRedirect } from "resources/preferences"
import { usePostQuery } from "./queries"
import { get } from "lodash"
import { combineTanStackStatuses } from "helpers/data-statuses"

/**
 * Provider value shape: { accountId, key, type, fallbackKey? }.
 * `fallbackKey` is a read-only migration shim: reads that find no row under `key` resolve
 * from `fallbackKey` instead (e.g. legacy unscoped "dashboard" rows). Saves always write `key`,
 * so the fallback stops applying per subtype as soon as the user saves under the scoped key.
 */
const PreferenceContext = createContext(null)

export default PreferenceContext

function getContextPreferenceQueryKey(accountId, contextKey, contextType, subType, fallbackKey) {
  return ["preferences", "context", accountId, contextKey ?? "", fallbackKey ?? "", contextType ?? "", subType].join(":")
}

export function useSaveContextPreferences(subType) {
  const context = useContext(PreferenceContext)
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const readQueryKey = useMemo(
    () => [getContextPreferenceQueryKey(context?.accountId, context?.key, context?.type, subType, context?.fallbackKey)],
    [context?.accountId, context?.key, context?.type, subType, context?.fallbackKey],
  )
  const savePreferenceMutation = useMutation({
    mutationFn: (value) => {
      const payload = {
        items: [{
          accountId: context.accountId,
          key: context.key,
          type: `${context.type}.${subType}`,
          value,
        }],
      }
      return new Promise((resolve, reject) => {
        dispatch(ApiWrapperCreators.put("/api/users/me/preferences", payload, resolve, reject))
      })
    },
    onSuccess: (_data, savedValue) => {
      // Include `key` so the fallback-aware read select resolves this row as the primary.
      queryClient.setQueryData(readQueryKey, [{ key: context.key, value: savedValue }])
    },
  })

  return savePreferenceMutation.mutate
}

export function saveContextPreferencesDual(primarySubType, mirrorSubType, savePrimary, saveMirror, value) {
  savePrimary(value)
  if (mirrorSubType != null && mirrorSubType !== primarySubType) {
    saveMirror(value)
  }
}

export function useSaveContextPreferencesDual(primarySubType, mirrorSubType) {
  const savePrimary = useSaveContextPreferences(primarySubType)
  const saveMirror = useSaveContextPreferences(mirrorSubType ?? primarySubType)

  return useCallback(
    (value) => {
      saveContextPreferencesDual(primarySubType, mirrorSubType, savePrimary, saveMirror, value)
    },
    [savePrimary, saveMirror, mirrorSubType, primarySubType],
  )
}

export function useReadContextPreference(subType, defaultValue, options = {}) {
  const { enabled = true } = options
  const accountId = useAccountId()
  const context = useContext(PreferenceContext)
  const fallbackKey = context?.fallbackKey
  const queryKey = useMemo(
    () => getContextPreferenceQueryKey(accountId, context?.key, context?.type, subType, fallbackKey),
    [accountId, context?.key, context?.type, subType, fallbackKey],
  )

  const items = useMemo(() => {
    const type = `${context.type}.${subType}`
    const baseItems = [{ accountId, key: context.key, type }]
    if (fallbackKey) {
      baseItems.push({ accountId, key: fallbackKey, type })
    }
    return baseItems
  }, [accountId, context.key, context.type, subType, fallbackKey])

  const queryResults = usePostQuery({
    url: "/api/users/me/preferences",
    payload: { items },
    key: queryKey,
    enabled: enabled && Boolean(accountId),
    // The server returns only the rows that exist (keyed by `key`), in no guaranteed order.
    // Presence-based resolution: a primary row always wins — even an empty `{}` (user cleared) —
    // so the fallback only applies while nothing was ever saved under the primary key.
    select: (queryResult) => {
      if (!fallbackKey) {
        return get(queryResult, "0.value", defaultValue)
      }
      const rows = queryResult ?? []
      const resolvedRow = rows.find((row) => row?.key === context.key)
        ?? rows.find((row) => row?.key === fallbackKey)
      return resolvedRow === undefined ? defaultValue : get(resolvedRow, "value", defaultValue)
    },
  })

  const queryStatus = combineTanStackStatuses([queryResults])

  return [queryResults.data, queryStatus]
}

/**
 * Saves a user preference scoped to the current account.
 */
export function useSavePreference(key, type) {
  const accountId = useAccountId()
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const readQueryKey = useMemo(
    () => [["preferences", accountId, key, type].join(":")],
    [accountId, key, type],
  )
  const savePreferenceMutation = useMutation({
    // Annotate so TS infers mutate's TVariables as unknown (not void) for typed callers.
    /** @param {unknown} value */
    mutationFn: (value) => {
      const payload = {
        items: [{
          accountId,
          key,
          type,
          value,
        }],
      }
      return new Promise((resolve, reject) => {
        dispatch(ApiWrapperCreators.put("/api/users/me/preferences", payload, resolve, reject))
      })
    },
    onSuccess: (_data, savedValue) => {
      queryClient.setQueryData(readQueryKey, [{ value: savedValue }])
    },
  })

  return savePreferenceMutation.mutate
}

/**
 * Retrieves a user preference scoped to the current account.
 * Pass `{ enabled: false }` to skip the read entirely — e.g. a flag-gated caller whose feature is off.
 */
export function useReadPreference(key, type, defaultValue, { enabled = true } = {}) {
  const accountId = useAccountId()
  const queryKey = useMemo(
    () => ["preferences", accountId, key, type].join(":"),
    [accountId, key, type],
  )

  const queryResults = usePostQuery({
    url: "/api/users/me/preferences",
    payload: {
      items: ([{
        accountId,
        key,
        type,
      }]),
    },
    key: queryKey,
    enabled: enabled && Boolean(accountId),
    select: (queryResult) => get(queryResult, "0.value", defaultValue),
  })

  const queryStatus = combineTanStackStatuses([queryResults])

  return [queryResults.data, queryStatus]
}

export function useCheckLastPreferenceRedirect() {
  const accountId = useAccountId()
  const { lastPreferenceRedirect } = useLastPreferenceRedirect()
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const wasRedirectedFromPreferences = useMemo(() => lastPreferenceRedirect && location.pathname.startsWith(lastPreferenceRedirect.redirectedTo), [lastPreferenceRedirect, location])

  return {
    wasRedirectedFromPreferences,
    deletePreferenceAndRedirectToDefault: () => {
      if (lastPreferenceRedirect?.preferenceType) {
        dispatch(PreferencesCreators.deletePreferences([{ type: lastPreferenceRedirect.preferenceType, accountId }]))
        if (lastPreferenceRedirect?.defaultRedirectTo) {
          navigate(lastPreferenceRedirect.defaultRedirectTo, { replace: true })
        }
      }
    },
  }
}
